import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { isApiError, type ApiError } from '@platform/api-client';
import { useApiClient } from '@platform/api-client/react';
import { StepUpDialog } from '@platform/ui';

import { subscribeToStepUp } from '../api/client';

import { describeApiError } from './describe-error';

/**
 * Step-up (M2.8, API.md §1.3, SECURITY.md §4.3, §6.3).
 *
 * The API marks the dangerous routes `@StepUp()`: starting impersonation, suspending a
 * tenant, changing a plan or a feature, editing the catalogue, promoting a super admin.
 * Each answers `403 MFA_STEP_UP_REQUIRED` when the last second factor is older than ten
 * minutes.
 *
 * Two paths lead here, and they are not the same thing:
 *
 * 1. **`guard(work)`** - the call site wraps the action. The refusal is caught, the
 *    dialog asks for a code, and then **the original call runs again** and its result is
 *    returned to the caller as if nothing had happened. This is the path every
 *    step-up action in the console uses, because a verification that forgets what it was
 *    for makes the operator start over.
 * 2. **The client callback** - anything that was not wrapped. There is nothing to retry,
 *    so the dialog simply says what is needed; the operator verifies and tries again.
 *    It exists so an un-wrapped call is a small annoyance rather than an unexplained
 *    "Forbidden".
 *
 * The second path deliberately stays quiet while the first is running: a guarded call
 * would otherwise open the dialog twice for the same refusal.
 */

export interface StepUpValue {
  /**
   * Runs `work`, and on `MFA_STEP_UP_REQUIRED` asks for a second factor and runs it
   * again. Any other failure is re-thrown untouched. A cancelled dialog rejects with the
   * original `ApiError`, so the call site's own error handling still sees a refusal.
   */
  guard: <Result>(work: () => Promise<Result>, action?: string) => Promise<Result>;
}

const StepUpContext = createContext<StepUpValue | null>(null);

interface PendingStepUp {
  action: string | undefined;
  /** Absent for the un-wrapped path: there is nothing to run again. */
  retry: (() => void) | null;
  cancel: () => void;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const api = useApiClient();
  const [pending, setPending] = useState<PendingStepUp | null>(null);

  /*
   * Non-zero while a guarded call is in flight. The client callback fires *before* the
   * error is thrown - i.e. before `guard` can catch it - so without this the un-wrapped
   * path would open a dialog that the guarded path is about to replace.
   */
  const guarding = useRef(0);

  useEffect(
    () =>
      subscribeToStepUp(() => {
        if (guarding.current > 0) return;
        setPending(
          (current) => current ?? { action: undefined, retry: null, cancel: () => undefined },
        );
      }),
    [],
  );

  const guard = useCallback<StepUpValue['guard']>(async (work, action) => {
    guarding.current += 1;
    try {
      return await work();
    } catch (caught) {
      if (!isApiError(caught) || !caught.isStepUpRequired) throw caught;

      await new Promise<void>((resolve, reject) => {
        setPending({
          action,
          retry: () => {
            resolve();
          },
          cancel: () => {
            reject(caught as ApiError);
          },
        });
      });

      // Verified. Run the original call again and hand its result back, so the caller
      // never learns that a dialog happened.
      return await work();
    } finally {
      guarding.current -= 1;
    }
  }, []);

  const value = useMemo<StepUpValue>(() => ({ guard }), [guard]);

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <StepUpDialog
        open={pending !== null}
        action={pending?.action}
        describeError={describeApiError}
        onClose={() => {
          pending?.cancel();
          setPending(null);
        }}
        onVerifyTotp={async (code) => {
          await api.auth.verifyTotp({ code });
        }}
        passkey={{
          getOptions: () => api.auth.passkeyAuthenticateOptions(),
          verify: (response) => api.auth.passkeyAuthenticate({ response }),
        }}
        onVerified={() => {
          pending?.retry?.();
          setPending(null);
        }}
      />
    </StepUpContext.Provider>
  );
}

/**
 * Throws outside the provider. A `guard` that silently ran the call unprotected would
 * turn a missing provider into a confusing 403 at the worst possible moment.
 */
export function useStepUp(): StepUpValue {
  const value = useContext(StepUpContext);
  if (value === null) throw new Error('useStepUp must be used inside a <StepUpProvider>');
  return value;
}
